import React, { useMemo, useState, useEffect, useRef, forwardRef, useImperativeHandle, useCallback } from 'react'

import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import { ReactComponent as AddCartIcon } from 'Assets/Icons/AddCart.svg'
import { ReactComponent as NotAvailableIcon } from 'Assets/Icons/SinDisponibilidad.svg'
import { PurchaseActions } from 'Redux/purchases'
import { MEASURE_UNIT, KEY_CODES, AVAILABILITY_STATES } from 'Config/constants'
import { getProduct } from 'Services/api'
import Trash from 'Assets/Icons/Trash'
import { needsApprovalMode } from 'Redux/approver/selectors'
import { ApproverActions } from 'Redux/approver'
import EraseProductButton from 'Components/atoms/eraseButton'
import AcceptProductButton from 'Components/atoms/acceptButton'
import SelectorButton from 'Components/atoms/selectorButton'
import { selectCenterId, floatModulus } from 'Redux/purchases/utils'
import AmountInput from 'Components/atoms/ammountInput'
import { NumberFormat } from 'Components/atoms'
import ModalAddExtraService from 'Components/molecules/modalExtraService'

import {
  ButtonCart,
  ButtonCartSustitution,
  ReplaceButton,
  ButtonCartRemove,
  RemoveButton,
  ButtonNotAvailable,
  Unavailable,
  EraseButton,
  MainContainer,
  ExtraServiceContainer,
  ButtonExtraCart,
  RowTB,
  ESPrice,
  ESDesc,
  ButtonCartNotAdd,
} from './styled'

const ProductButton = forwardRef((props, ref) => {
  const {
    name,
    provider,
    price,
    min,
    multiple,
    measureUnit,
    pricePerKg,
    id,
    isSimpleCard,
    rawMaterial,
    notAvailable,
    reference,
    setShowModalConfirmationProduct,
    setOnConfirmProduct,
    showJustButtonCard,
    availability,
    fromModal,
    innerRef,
    onNext,
    onPrevious,
    isServicioExtra,
    extraService,
    isHorizontal,
  } = props

  const { t } = useTranslation()
  const dispatch = useDispatch()
  const inputRef = useRef()

  const [openAddExtraService, setOpenAddExtraService] = useState(false)
  const [extraServiceDesc, setExtraServiceDesc] = useState(extraService?.description || null)
  const [extraServicePrice, setExtraServicePrice] = useState(extraService?.price?.toString()?.replace('.', ',') || null)

  const isLoadingCart = useSelector(({ purchase }) => purchase.isLoadingCart)
  const centerId = useSelector(selectCenterId)
  const needsApproval = useSelector(needsApprovalMode)

  const reducerKey = needsApproval ? 'approver' : 'purchase'
  const { amount = 0 } = useSelector(state => state[reducerKey].cart[id]) || {}
  const inCart = useSelector(state => state[reducerKey].cart[id]) || null
  const cart = useSelector(state => state[reducerKey].cart)

  const [inputValue, setInputValue] = useState(amount || 0)
  const [focused, setFocused] = useState(false)
  const [error, setError] = useState(false)

  const hasCartExtraService = useMemo(
    () =>
      Object.values(cart).some(
        product =>
          product?.isServicioExtra ||
          (product?.extraServiceDescription && product.extraServiceDescription !== '') ||
          product?.extraService != null
      ),
    [cart]
  )

  const productInCart = useMemo(() => !!cart?.[id], [cart, id])

  const alternativeReference = availability ? availability.alternative_reference : null
  const status = availability ? availability.status : AVAILABILITY_STATES.AVAILABLE

  useImperativeHandle(innerRef, () => ({
    focus() {
      setFocused(true)
    },
    hasInput() {
      return (
        amount !== 0 &&
        status !== AVAILABILITY_STATES.UNAVAILABLE &&
        status !== AVAILABILITY_STATES.NEXT &&
        status !== AVAILABILITY_STATES.NOT_STOCK
      )
    },
    getId() {
      return id
    },
  }))

  useEffect(() => {
    if (amount !== undefined) setInputValue(amount)
  }, [amount])

  useEffect(() => {
    if (focused && inputRef && inputRef.current && inputRef.current.select) {
      inputRef.current.select()
    }
  }, [focused, innerRef])

  const setFocusInput = () => setFocused(true)

  const onOpenExtraServiceModal = useCallback(
    e => {
      e.stopPropagation()
      if (amount === 0) setOpenAddExtraService(true)
    },
    [amount, setOpenAddExtraService]
  )

  const onChangeInput = useCallback(
    e => {
      const text = e.target.value?.replace(',', '.')
      if (!text) {
        setInputValue('')
        setError(true)
        return
      }

      const newValue = parseFloat(text) || 0
      setInputValue(newValue)
      const priceModulus = floatModulus(newValue, multiple)
      // eslint-disable-next-line eqeqeq
      if (text && (newValue < min || priceModulus !== 0) && newValue != 0) {
        setError(true)
        return
      }
      setError(false)
    },
    [min, multiple]
  )

  // TODO: CHECK VALE Revisar si se envía precio o sólo precio extra
  const onUpdateExtraServiceInfo = useCallback(() => {
    const editCart = needsApproval ? ApproverActions.editCart : PurchaseActions.addProductToCart
    if (isServicioExtra) {
      const eSP = extraServicePrice ? extraServicePrice?.replace(',', '.') : extraService?.price || 0
      const xServicePrice = parseFloat(eSP) || 0
      dispatch(
        editCart(id, {
          amount: 1,
          name,
          total: 0,
          provider,
          price: 0,
          pricePerKg,
          measureUnit: MEASURE_UNIT.UN,
          min,
          multiple: 1,
          id,
          raw_material: rawMaterial,
          state: status,
          reference,
          extraServiceDescription:
            extraServiceDesc && extraServiceDesc !== '' ? extraServiceDesc : extraService?.description || null,
          extraServicePrice: xServicePrice,
          isServicioExtra: isServicioExtra || undefined,
        })
      )
    }
    //  dispatch(fetchCart())
    setOpenAddExtraService(false)
  }, [
    dispatch,
    extraService,
    extraServiceDesc,
    extraServicePrice,
    id,
    isServicioExtra,
    min,
    name,
    needsApproval,
    pricePerKg,
    provider,
    rawMaterial,
    reference,
    status,
  ])

  const updateProductAmount = useCallback(
    newAmount => {
      const editCart = needsApproval ? ApproverActions.editCart : PurchaseActions.addProductToCart
      if (!isServicioExtra || newAmount === 0) {
        dispatch(
          editCart(id, {
            amount: newAmount,
            name,
            total: newAmount * price,
            provider,
            price,
            pricePerKg,
            measureUnit,
            min,
            multiple,
            id,
            raw_material: rawMaterial,
            state: status,
            reference,
          })
        )
      }
    },
    [
      dispatch,
      id,
      isServicioExtra,
      measureUnit,
      min,
      multiple,
      name,
      needsApproval,
      price,
      pricePerKg,
      provider,
      rawMaterial,
      reference,
      status,
    ]
  )

  const onConfirmProductErase = useCallback(() => {
    updateProductAmount(0)
    setShowModalConfirmationProduct(false)
    setFocused(false)
  }, [setShowModalConfirmationProduct, updateProductAmount])

  const setToZero = useCallback(() => {
    if (isSimpleCard && !notAvailable) {
      setShowModalConfirmationProduct(true)
      setOnConfirmProduct(onConfirmProductErase)
      return
    }
    setFocused(false)

    updateProductAmount(0)
  }, [
    isSimpleCard,
    notAvailable,
    onConfirmProductErase,
    setOnConfirmProduct,
    setShowModalConfirmationProduct,
    updateProductAmount,
  ])

  const onBlurInput = useCallback(() => {
    const newValue = parseFloat(inputValue) || 0
    const priceModulus = floatModulus(newValue, multiple)

    setFocused(false)
    setError(false)

    if (newValue === 0) {
      setInputValue(amount)
      setToZero()
      return
    }

    // eslint-disable-next-line eqeqeq
    if ((newValue >= min && priceModulus === 0) || !newValue) {
      const parsedInput = Number.isNaN(newValue) ? 0 : newValue
      updateProductAmount(parsedInput)
      return
    }
    setInputValue(amount)
  }, [amount, inputValue, min, multiple, setToZero, updateProductAmount])

  const add = useCallback(sum => updateProductAmount(+(amount + sum).toFixed(2)), [amount, updateProductAmount])

  const susbtract = useCallback(sub => updateProductAmount(+(amount - sub).toFixed(2)), [amount, updateProductAmount])

  const onClickAdd = e => {
    e.stopPropagation()
    if (!amount) {
      updateProductAmount(min)
    }
  }

  const onClickSubstract = useCallback(() => {
    if (amount - multiple <= 0) {
      return setToZero()
    }

    return susbtract(multiple)
  }, [amount, multiple, setToZero, susbtract])

  const handleKeydown = useCallback(
    e => {
      if (e.keyCode === KEY_CODES.UP_ARROW) {
        e.preventDefault()
        add(multiple)
      } else if (e.keyCode === KEY_CODES.DOWN_ARROW) {
        e.preventDefault()
        onClickSubstract()
      } else if (e.keyCode === KEY_CODES.TAB && e.shiftKey) {
        e.preventDefault()
        inputRef.current.blur()
        setFocused(false)
        onPrevious(id)
      } else if (e.keyCode === KEY_CODES.TAB || e.keyCode === KEY_CODES.ENTER) {
        e.preventDefault()
        inputRef.current.blur()
        setFocused(false)
        onNext(id)
      }
    },
    [add, id, multiple, onClickSubstract, onNext, onPrevious]
  )

  const renderContent = (buttonCopy, flag, icon) => (
    <>
      {icon === 'unavailable' ? (
        <NotAvailableIcon style={{ marginRight: '0.5rem' }} />
      ) : (
        <AddCartIcon style={{ marginRight: '0.5rem' }} />
      )}
      <span>{buttonCopy}</span>
      {flag ? <span className='flag'>{flag}</span> : null}
    </>
  )

  const renderSelectors = useCallback(
    () => (
      <>
        <AmountInput
          onClick={setFocusInput}
          focused={focused}
          onBlur={onBlurInput}
          inputRef={inputRef}
          value={inputValue}
          onChange={!isServicioExtra || !amount ? onChangeInput : null}
          onKeyDown={!isServicioExtra || !amount ? handleKeydown : null}
          suffix={measureUnit}
        />
        {focused ? (
          <AcceptProductButton onClick={() => setFocused(false)} />
        ) : (
          <div style={{ display: 'flex' }}>
            <SelectorButton onClick={onClickSubstract} icon='down' />
            {isServicioExtra && amount > 0 ? null : <SelectorButton onClick={() => add(multiple)} icon='up' />}
          </div>
        )}
      </>
    ),
    [
      add,
      amount,
      focused,
      handleKeydown,
      inputValue,
      isServicioExtra,
      measureUnit,
      multiple,
      onBlurInput,
      onChangeInput,
      onClickSubstract,
    ]
  )

  const onReplace = async e => {
    e.stopPropagation()
    if (alternativeReference) {
      try {
        const response = await getProduct(centerId, alternativeReference)
        dispatch(
          PurchaseActions.setProduct({
            ...response,
            measureUnit: response.measure_unit,
            previousPrice: response.previous_price,
            pricePerKg: response.price_per_kg,
            rawMaterial: response.raw_material,
          })
        )
        dispatch(PurchaseActions.setModalProductOpen(true))
      } catch (error2) {
        // eslint-disable-next-line no-console
        console.log(error)
      }
    }
  }

  const hasReplace = (alternativeReference && alternativeReference !== '') || false
  const willPurchase = inCart != null

  const onRemove = useCallback(
    e => {
      e.stopPropagation()
      return setToZero()
    },
    [setToZero]
  )

  const showUnavailableOptions = (unavailableCopy, reverse) => {
    if (isSimpleCard) {
      return (
        <ButtonCartRemove reverse={reverse} onClick={onRemove}>
          <span>{unavailableCopy}</span>
          <RemoveButton reverse={reverse}>{t('delete')}</RemoveButton>
        </ButtonCartRemove>
      )
    }

    if (hasReplace && !fromModal) {
      return (
        <ButtonCartSustitution reverse={reverse}>
          <span>{unavailableCopy}</span>
          <ReplaceButton onClick={onReplace} reverse={reverse}>
            {t('substitute')}
          </ReplaceButton>
        </ButtonCartSustitution>
      )
    }
    return (
      <ButtonCart reverse={reverse} focused={focused}>
        <span>{unavailableCopy}</span>
      </ButtonCart>
    )
  }

  const showSimpleCartButton = () => (
    <ButtonCart onClick={onClickAdd} hasAmount={!!amount} error={error} focused={focused}>
      {!amount ? renderContent() : renderSelectors()}
      {amount ? <EraseProductButton onClick={onRemove} /> : null}
    </ButtonCart>
  )

  const onOpenExtraServiceCart = useCallback(
    e => {
      e.stopPropagation()
      setOpenAddExtraService(true)
    },
    [setOpenAddExtraService]
  )

  const showExtraServiceButton = useCallback(
    () => (
      <ExtraServiceContainer isHorizontal={isHorizontal} isExtraService={isServicioExtra}>
        <ButtonCart onClick={onOpenExtraServiceModal} hasAmount={!!amount} error={error} focused={focused}>
          {!amount ? renderContent(t('addCart')) : renderSelectors()}
          {amount ? <EraseProductButton onClick={onRemove} /> : null}
        </ButtonCart>
        {isSimpleCard && !isHorizontal && (isServicioExtra || extraServiceDesc) && (
          <>
            {(extraService?.description || extraServiceDesc) && (
              <RowTB>
                <ESDesc>{extraService?.description || extraServiceDesc} </ESDesc>
                <ESPrice>
                  <NumberFormat value={extraService?.price || extraServicePrice} suffix='€' />
                </ESPrice>
              </RowTB>
            )}
            <ButtonExtraCart onClick={onOpenExtraServiceCart} variant='outlined'>
              {extraService?.description || extraServiceDesc ? t('editESDesc') : t('addExtra')}
            </ButtonExtraCart>
          </>
        )}
      </ExtraServiceContainer>
    ),
    [
      isHorizontal,
      onOpenExtraServiceModal,
      amount,
      error,
      focused,
      t,
      renderSelectors,
      onRemove,
      isSimpleCard,
      isServicioExtra,
      extraServiceDesc,
      extraService,
      extraServicePrice,
      onOpenExtraServiceCart,
    ]
  )

  const showSelector = e => {
    e.stopPropagation()
    onClickAdd(e)
  }

  const showLastUnitsOptions = (unavailableCopy, reverse) => {
    if (isSimpleCard) {
      return (
        <ButtonCartRemove reverse={reverse} onClick={onRemove}>
          <span>{unavailableCopy}</span>
          <RemoveButton reverse={reverse}>{t('delete')}</RemoveButton>
        </ButtonCartRemove>
      )
    }

    if (willPurchase && amount > 0) {
      return (
        <ButtonCart onClick={onClickAdd} hasAmount={!!amount} error={error} focused={focused}>
          {!amount ? renderContent(t('lastItems')) : renderSelectors()}
          {amount ? <EraseProductButton onClick={onRemove} /> : null}
        </ButtonCart>
      )
    }
    if (hasReplace && !fromModal) {
      return (
        <ButtonCartSustitution reverse={reverse} onClick={showSelector}>
          <span>{unavailableCopy}</span>
          <ReplaceButton onClick={onReplace} reverse={reverse}>
            {t('substitute')}
          </ReplaceButton>
        </ButtonCartSustitution>
      )
    }

    return (
      <ButtonCart onClick={onClickAdd} hasAmount={!!amount} error={error} focused={focused}>
        {!amount ? renderContent(t('lastItems')) : renderSelectors()}
        {amount ? <EraseProductButton onClick={onRemove} /> : null}
      </ButtonCart>
    )
  }

  const showButton = () => {
    if (hasCartExtraService && !isServicioExtra && !productInCart) {
      return (
        <ButtonCartNotAdd className='disabled'>
          <div className='default-text'>{renderContent(t('addCart'), '')}</div>
          <div className='hover-text'>{renderContent(t('withExtraService'), '')}</div>
        </ButtonCartNotAdd>
      )
    }
    if (Object?.keys(cart)?.length > 0 && !hasCartExtraService && isServicioExtra) {
      return (
        <ButtonCartNotAdd className='disabled'>
          <div className='default-text'>{renderContent(t('addCart'), '')}</div>
          <div className='hover-text'>{renderContent(t('cartWithProducts'), '')}</div>
        </ButtonCartNotAdd>
      )
    }

    if (isServicioExtra) {
      return showExtraServiceButton(isSimpleCard ? null : t('addCart'))
    }

    if (hasCartExtraService && !isServicioExtra && !willPurchase) {
      return <ButtonCart className='disabled'>{renderContent(t('withExtraService'), '', 'unavailable')}</ButtonCart>
    }

    if (availability) {
      if (status === AVAILABILITY_STATES.AVAILABLE) {
        if (isSimpleCard) {
          return showSimpleCartButton()
        }
        return (
          <ButtonCart onClick={onClickAdd} hasAmount={!!amount} error={error} focused={focused}>
            {!amount ? renderContent(t('addCart')) : renderSelectors()}
            {amount ? <EraseProductButton onClick={onRemove} /> : null}
          </ButtonCart>
        )
      }

      if (status === AVAILABILITY_STATES.UNAVAILABLE) {
        //not available any more
        if (willPurchase || hasReplace) {
          return showUnavailableOptions(t('deleted'))
        }
        return <ButtonCart className='disabled'>{renderContent(t('deleted'), '', 'unavailable')}</ButtonCart>
      }

      if (status === AVAILABILITY_STATES.NEXT) {
        if (willPurchase || hasReplace) {
          return showUnavailableOptions(t('nextAvailability'))
        }

        return <ButtonCart className='disabled'>{renderContent(t('nextAvailability'), '', 'unavailable')}</ButtonCart>
      }

      if (status === AVAILABILITY_STATES.NOT_STOCK) {
        if (!hasReplace && willPurchase && amount > 0) {
          return (
            <ButtonNotAvailable>
              <Unavailable>
                {availability && availability.status === AVAILABILITY_STATES.NOT_STOCK && t('noItems')}
                {availability && availability.status === AVAILABILITY_STATES.UNAVAILABLE && t('deleted')}
              </Unavailable>
              <EraseButton onClick={setToZero}>
                <Trash className='trash' width={22} fill='#fff' /> {t('delete')}
              </EraseButton>
            </ButtonNotAvailable>
          )
        }
        if (willPurchase || hasReplace) {
          return showUnavailableOptions(t('noItems'))
        }

        return <ButtonCart className='disabled'>{renderContent(t('noItems'), '', 'unavailable')}</ButtonCart>
      }

      if (status === AVAILABILITY_STATES.NEW) {
        if (isSimpleCard) {
          return showSimpleCartButton()
        }
        return (
          <ButtonCart onClick={onClickAdd} hasAmount={!!amount} error={error} focused={focused}>
            {!amount ? renderContent(t('addCart'), t('new')) : renderSelectors()}
            {amount ? <EraseProductButton onClick={onRemove} /> : null}
          </ButtonCart>
        )
      }

      if (status === AVAILABILITY_STATES.LAST_UNITS) {
        if (isSimpleCard) {
          return showSimpleCartButton()
        }
        if (willPurchase || hasReplace) {
          return showLastUnitsOptions(t('lastItems'), 'reverse')
        }

        return (
          <ButtonCart onClick={onClickAdd} hasAmount={!!amount} error={error} focused={focused}>
            {!amount ? renderContent(t('lastItems')) : renderSelectors()}
            {amount ? <EraseProductButton onClick={onRemove} /> : null}
          </ButtonCart>
        )
      }
    }

    if (isSimpleCard) {
      return showSimpleCartButton()
    }

    return (
      <ButtonCart onClick={onClickAdd} hasAmount={!!amount} error={error} focused={focused} ref={ref}>
        {!amount ? renderContent(t('addCart')) : renderSelectors()}
        {amount ? <EraseProductButton onClick={onRemove} /> : null}
      </ButtonCart>
    )
  }

  if (showJustButtonCard) {
    // TODO: No tiene return, se puede quitar?
    showButton()
  }

  return (
    <MainContainer disabled={isLoadingCart}>
      {showButton()}

      <ModalAddExtraService
        showExtraServiceModal={openAddExtraService}
        setShowExtraServiceModal={setOpenAddExtraService}
        extraServiceDesc={
          extraServiceDesc && extraServiceDesc !== '' ? extraServiceDesc : extraService?.description || null
        }
        setExtraServiceDesc={setExtraServiceDesc}
        extraServicePrice={
          extraServicePrice ||
          (extraServicePrice === null && extraService?.price ? extraService.price?.toString()?.replace('.', ',') : '')
        }
        setExtraServicePrice={setExtraServicePrice}
        isServicioExtra={isServicioExtra}
        onUpdateExtraServiceInfo={onUpdateExtraServiceInfo}
        isEdit={extraService != null}
      />
    </MainContainer>
  )
})

ProductButton.defaultProps = {
  name: '',
  provider: {},
  price: 0,
  min: 0,
  multiple: 0,
  measureUnit: '',
  pricePerKg: 0,
  id: '',
  isSimpleCard: false,
  rawMaterial: 'food',
  notAvailable: false,
  reference: '',
  setOnConfirmProduct: () => null,
  setShowModalConfirmationProduct: () => null,
  showJustButtonCard: false,
  availability: null,
  fromModal: false,
  innerRef: null,
  onNext: () => null,
  onPrevious: () => null,
  isServicioExtra: false,
  extraService: null,
  isHorizontal: false,
}

ProductButton.propTypes = {
  name: PropTypes.string,
  provider: PropTypes.object,
  price: PropTypes.number,
  min: PropTypes.number,
  multiple: PropTypes.number,
  measureUnit: PropTypes.string,
  pricePerKg: PropTypes.number,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isSimpleCard: PropTypes.bool,
  rawMaterial: PropTypes.string,
  notAvailable: PropTypes.bool,
  reference: PropTypes.string,
  setOnConfirmProduct: PropTypes.func,
  setShowModalConfirmationProduct: PropTypes.func,
  showJustButtonCard: PropTypes.bool,
  availability: PropTypes.object,
  fromModal: PropTypes.bool,
  innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  onNext: PropTypes.func,
  onPrevious: PropTypes.func,
  isServicioExtra: PropTypes.bool,
  extraService: PropTypes.object,
  isHorizontal: PropTypes.bool,
}

export default ProductButton
